/**
 * Sync Status Store
 *
 * Manages sync progress state for the UI.
 * Shows real-time progress during initial sync (180k+ sessions).
 *
 * NOT persisted - memory only.
 *
 * @module core/store/useSyncStatusStore
 */

import { create } from "zustand";
import type { SyncProgress } from "~/core/sync/sync.worker";

// ============================================================================
// TYPES
// ============================================================================

export interface SyncStatusState {
  // Progress
  phase: SyncProgress["phase"];
  pulled: number;
  totalToPull: number;
  pushed: number;
  totalToPush: number;
  error: string | undefined;
  /**
   * ISSUE_187: 현재 진행 중인 동기화가 "어느 학원"인지. 학원 전환 시 worker를
   * 재조준하며 beginSync(academyId)로 스탬프 → 런처 카드가 자기 학원 게이지인지 식별.
   * null = 아직 특정 학원 sync가 시작되지 않음.
   */
  academyId: string | null;

  // UI State
  isPanelOpen: boolean;
  hasSeenInitialSync: boolean;
  isMinimized: boolean;
  /**
   * ISSUE_187: 런처(`/spaces`)에서 카드 인라인 게이지로 진행률을 보여주는 동안 true.
   * true면 중앙 모달(SyncStatusPanel) 자동 오픈을 억제 — 카드가 표시를 담당.
   */
  launcherMode: boolean;
}

export interface SyncStatusActions {
  // Progress
  updateProgress: (progress: SyncProgress) => void;
  resetProgress: () => void;
  /**
   * ISSUE_187: 특정 학원 동기화 시작 — 진행률 리셋 + academyId 스탬프.
   * SyncInitializer가 worker (재)조준 직전 호출.
   */
  beginSync: (academyId: string) => void;

  // UI State
  openPanel: () => void;
  closePanel: () => void;
  minimizePanel: () => void;
  markInitialSyncSeen: () => void;
  /** ISSUE_187: 런처 카드 게이지 모드 토글 (중앙 모달 자동오픈 억제) */
  setLauncherMode: (value: boolean) => void;
}

export type SyncStatusStore = SyncStatusState & SyncStatusActions;

// ============================================================================
// CONSTANTS
// ============================================================================

/** Threshold for showing sync panel automatically (1000+ sessions = initial sync) */
const INITIAL_SYNC_THRESHOLD = 1000;

// ============================================================================
// DEFAULT STATE
// ============================================================================

const DEFAULT_STATE: SyncStatusState = {
  phase: "idle",
  pulled: 0,
  totalToPull: 0,
  pushed: 0,
  totalToPush: 0,
  error: undefined,
  academyId: null,
  isPanelOpen: false,
  hasSeenInitialSync: false,
  isMinimized: false,
  launcherMode: false,
};

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useSyncStatusStore = create<SyncStatusStore>((set, get) => ({
  ...DEFAULT_STATE,

  updateProgress: (progress) => {
    const state = get();

    // Detect initial sync: large pull AND not seen before
    const isInitialSync =
      progress.totalToPull > INITIAL_SYNC_THRESHOLD && !state.hasSeenInitialSync;

    // Auto-open panel for initial sync (if not minimized)
    // ISSUE_187: 런처 모드에선 카드 인라인 게이지가 담당 → 중앙 모달 억제
    const shouldAutoOpen =
      isInitialSync &&
      progress.phase === "pulling" &&
      !state.isPanelOpen &&
      !state.isMinimized &&
      !state.launcherMode;

    set({
      phase: progress.phase,
      pulled: progress.pulled,
      totalToPull: progress.totalToPull,
      pushed: progress.pushed,
      totalToPush: progress.totalToPush,
      error: progress.error,
      ...(shouldAutoOpen ? { isPanelOpen: true } : {}),
    });
  },

  resetProgress: () => {
    set({
      phase: "idle",
      pulled: 0,
      totalToPull: 0,
      pushed: 0,
      totalToPush: 0,
      error: undefined,
    });
  },

  beginSync: (academyId) => {
    // 학원 (재)조준: 진행률 0으로 리셋 + 어느 학원인지 스탬프
    set({
      phase: "idle",
      pulled: 0,
      totalToPull: 0,
      pushed: 0,
      totalToPush: 0,
      error: undefined,
      academyId,
    });
  },

  openPanel: () => {
    set({ isPanelOpen: true, isMinimized: false });
  },

  closePanel: () => {
    set({ isPanelOpen: false });
  },

  minimizePanel: () => {
    set({ isPanelOpen: false, isMinimized: true });
  },

  markInitialSyncSeen: () => {
    set({ hasSeenInitialSync: true });
  },

  setLauncherMode: (value) => {
    set({ launcherMode: value });
  },
}));

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

/**
 * Get sync phase
 */
export const useSyncPhase = () => useSyncStatusStore((state) => state.phase);

/**
 * Get pulled count
 */
export const useSyncPulled = () => useSyncStatusStore((state) => state.pulled);

/**
 * Get total to pull
 */
export const useSyncTotalToPull = () =>
  useSyncStatusStore((state) => state.totalToPull);

/**
 * Get sync error
 */
export const useSyncError = () => useSyncStatusStore((state) => state.error);

/**
 * ISSUE_187: 현재 동기화 중인 학원 ID (런처 카드가 자기 게이지인지 식별)
 */
export const useSyncAcademyId = () =>
  useSyncStatusStore((state) => state.academyId);

/**
 * Get panel open state
 */
export const useIsSyncPanelOpen = () =>
  useSyncStatusStore((state) => state.isPanelOpen);

/**
 * Get panel actions
 */
export const useOpenSyncPanel = () =>
  useSyncStatusStore((state) => state.openPanel);

export const useCloseSyncPanel = () =>
  useSyncStatusStore((state) => state.closePanel);

export const useMinimizeSyncPanel = () =>
  useSyncStatusStore((state) => state.minimizePanel);

/**
 * Check if currently syncing
 */
export const useIsSyncing = () =>
  useSyncStatusStore(
    (state) => state.phase === "pulling" || state.phase === "pushing"
  );

/**
 * Get sync progress percentage (0-100)
 */
export const useSyncProgressPercent = () =>
  useSyncStatusStore((state) => {
    if (state.totalToPull === 0) return 0;
    return Math.min(100, Math.round((state.pulled / state.totalToPull) * 100));
  });

export default useSyncStatusStore;
